import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Star, Flag, Eye, EyeOff, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate } from '@/lib/utils-helpers';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tab, setTab] = useState('all');

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (tab === 'flagged') params.flagged = true;
      const res = await api.get('/admin/reviews', { params });
      setReviews(res.data.reviews || []);
      setTotalPages(res.data.pages || 1);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const flagReview = async (id) => { try { await api.put(`/admin/reviews/${id}/flag`); toast.success('Review flagged'); fetchReviews(); } catch { toast.error('Failed'); } };
  const hideReview = async (id) => { try { await api.put(`/admin/reviews/${id}/hide`); toast.success('Review hidden'); fetchReviews(); } catch { toast.error('Failed'); } };
  const approveReview = async (id) => { try { await api.put(`/admin/reviews/${id}/approve`); toast.success('Review approved'); fetchReviews(); } catch { toast.error('Failed'); } };

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => <Star key={i} className={`h-3 w-3 ${i <= rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />)}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Review Moderation</h1>
        <p className="text-muted-foreground mt-1">Flag, hide, or approve user reviews.</p>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setPage(1); }}>
        <TabsList>
          <TabsTrigger value="all" data-testid="reviews-tab-all">All Reviews</TabsTrigger>
          <TabsTrigger value="flagged" data-testid="reviews-tab-flagged">Flagged</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}</div>
          ) : (
            <div className="space-y-3">
              {reviews.map(review => (
                <div key={review.id} className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl border" data-testid={`review-${review.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {renderStars(review.rating)}
                      <span className="text-xs text-muted-foreground">{formatDate(review.created_at)}</span>
                      {review.is_flagged && <Badge variant="outline" className="bg-rose-50 text-rose-800 text-xs">Flagged</Badge>}
                      {review.is_hidden && <Badge variant="outline" className="bg-slate-100 text-slate-600 text-xs">Hidden</Badge>}
                    </div>
                    <p className="text-sm mb-1">{review.comment}</p>
                    <p className="text-xs text-muted-foreground">By: {review.reviewer_name} | For: {review.target_name} ({review.target_type})</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {!review.is_flagged && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => flagReview(review.id)}><Flag className="h-3 w-3 mr-1" /> Flag</Button>}
                    {!review.is_hidden && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => hideReview(review.id)}><EyeOff className="h-3 w-3 mr-1" /> Hide</Button>}
                    {(review.is_flagged || review.is_hidden) && <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => approveReview(review.id)}><CheckCircle2 className="h-3 w-3 mr-1" /> Approve</Button>}
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-center py-8 text-muted-foreground">No reviews found</p>}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
